import type { PaletteCommand } from "./CommandPalette";

/** 0 = word-prefix match (best), 1 = subsequence match, null = no match. */
function matchRank(haystack: string, query: string): 0 | 1 | null {
  const words = haystack.split(/\s+/);
  if (words.some((w) => w.startsWith(query))) return 0;

  let i = 0;
  for (const ch of haystack) {
    if (i < query.length && ch === query[i]) i += 1;
  }
  return i === query.length ? 1 : null;
}

function commandRank(command: PaletteCommand, query: string): 0 | 1 | null {
  const haystacks = [command.label, ...(command.keywords ?? [])];
  let best: 0 | 1 | null = null;
  for (const haystack of haystacks) {
    const rank = matchRank(haystack.toLowerCase(), query);
    if (rank === 0) return 0;
    if (rank === 1) best = 1;
  }
  return best;
}

/**
 * Pure command scorer — case-insensitive word-prefix and subsequence match
 * over `label` + `keywords`. Prefix hits rank above subsequence-only hits;
 * non-matches are dropped. Empty/whitespace query returns every command
 * unchanged (input order). Ties keep input order (stable sort).
 */
export function filterCommands(commands: PaletteCommand[], query: string): PaletteCommand[] {
  const q = query.trim().toLowerCase();
  if (!q) return commands;

  return commands
    .map((command, index) => ({ command, index, rank: commandRank(command, q) }))
    .filter(
      (entry): entry is { command: PaletteCommand; index: number; rank: 0 | 1 } =>
        entry.rank !== null
    )
    .sort((a, b) => (a.rank !== b.rank ? a.rank - b.rank : a.index - b.index))
    .map((entry) => entry.command);
}
